import { readMe, updateMe } from '@directus/sdk';
import { useMutation, useQuery } from '@tanstack/react-query';

import { queryKeysFactory } from '../../lib/queryKeysFactory.ts';
import { auth, directusClient } from '../../services/directusClient/directusClient.ts';
import { tanstackQueryClient } from '../../services/tanstackQueryClient.ts';

class AuthenticationError extends Error {
  constructor() {
    super('Not authenticated');
    this.name = 'AuthenticationError';
  }
}

export const useMe = () => {
  const {
    data,
    isPending: queryIsPending,
    error,
  } = useQuery({
    queryKey: queryKeysFactory('user').detail('me'),
    staleTime: 60 * (60 * 1000), // 60 mins
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
    queryFn: async () => {
      try {
        const user = await directusClient.request(readMe());

        if (!user) {
          throw new AuthenticationError();
        }

        return user;
      } catch (error) {
        if (
          error instanceof AuthenticationError ||
          (error &&
            typeof error === 'object' &&
            'errors' in error &&
            Array.isArray((error as { errors: { extensions?: { code?: string } }[] }).errors) &&
            (error as { errors: { extensions?: { code?: string } }[] }).errors.some(
              (e) => e.extensions?.code === 'INVALID_CREDENTIALS' || e.extensions?.code === 'TOKEN_EXPIRED',
            ))
        ) {
          throw new AuthenticationError();
        }
        throw error;
      }
    },
  });

  const { mutate, isPending: mutationIsPending } = useMutation({
    mutationFn: (data: Record<string, unknown>) => directusClient.request(updateMe(data)),
    onSuccess: async () => {
      const queryKey = queryKeysFactory('user').detail('me');

      await tanstackQueryClient.invalidateQueries({ queryKey });
    },
  });

  const isAuthError = error instanceof AuthenticationError;

  return {
    data,
    isPending: queryIsPending || mutationIsPending,
    isAuthError,
    error,
    mutate,
    queryIsPending,
    mutationIsPending,
  };
};

export const useLogin = <ErrorData>({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Awaited<ReturnType<typeof auth.login>>) => void;
  onError?: (data: ErrorData) => void;
}) => {
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => auth.login(email, password),
    onSuccess: async (data) => {
      const queryKey = queryKeysFactory('user').detail('me');

      await tanstackQueryClient.invalidateQueries({ queryKey });

      onSuccess?.(data);
    },
    onError,
  });
};

export const useLogout = () => {
  return useMutation({
    mutationFn: () => auth.logout(),
    onSuccess: () => {
      localStorage.removeItem('selected_user_id');
      tanstackQueryClient.clear();
    },
  });
};
